import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const isCancel = location.pathname.includes("cancel");
  const [state, setState] = useState(isCancel ? "cancelled" : "checking");
  const [orderNumber, setOrderNumber] = useState(localStorage.getItem("skk_last_order") || "");

  useEffect(() => {
    if (isCancel) return;
    const sid = new URLSearchParams(location.search).get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0;
    let timer;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.order_number) setOrderNumber(r.data.order_number);
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (r.data.status === "expired" || r.data.payment_status === "failed") { setState("error"); return; }
      } catch (e) {
        console.warn("payment status check failed, retrying", e);
      }
      if (cancelled) return;
      if (tries++ < 8) timer = setTimeout(poll, 1600);
      else setState("pending");
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isCancel, location.search]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-5">
      <div className="rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-10 max-w-md w-full text-center">
        {state === "checking" && (<><Loader2 className="w-14 h-14 text-amber-400 mx-auto animate-spin" /><h1 className="font-display font-black text-2xl uppercase mt-4">Confirming Payment…</h1></>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" />
          <h1 className="font-display font-black text-3xl uppercase mt-4">Order Confirmed!</h1>
          <p className="text-[#FDFBF7]/75 mt-3">Thank you! Your Sir. King Kitchen order {orderNumber && <b className="text-amber-400">#{orderNumber}</b>} is confirmed. A confirmation email is on its way.</p>
          <div className="flex flex-col gap-2 mt-6">
            <Link to="/track" className="py-3.5 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide">Track My Order</Link>
            <button onClick={() => navigate("/")} className="py-3.5 rounded-full border border-emerald-700/50 text-amber-300 font-display font-bold uppercase tracking-wide">Back Home</button>
          </div>
        </>)}
        {(state === "pending") && (<>
          <Loader2 className="w-14 h-14 text-amber-400 mx-auto animate-spin" />
          <h1 className="font-display font-black text-2xl uppercase mt-4">Almost There</h1>
          <p className="text-[#FDFBF7]/75 mt-3">Your payment is processing. Track your order {orderNumber && <b>#{orderNumber}</b>} shortly.</p>
          <Link to="/track" className="inline-block mt-6 py-3.5 px-8 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide">Track Order</Link>
        </>)}
        {state === "cancelled" && (<>
          <XCircle className="w-16 h-16 text-red-400 mx-auto" />
          <h1 className="font-display font-black text-3xl uppercase mt-4">Checkout Cancelled</h1>
          <p className="text-[#FDFBF7]/75 mt-3">No worries — your cravings are safe. Try again whenever you're ready.</p>
          <button onClick={() => navigate("/menu")} className="mt-6 py-3.5 px-8 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide">Back To Menu</button>
        </>)}
        {state === "error" && (<>
          <XCircle className="w-16 h-16 text-red-400 mx-auto" />
          <h1 className="font-display font-black text-3xl uppercase mt-4">Something Went Wrong</h1>
          <p className="text-[#FDFBF7]/75 mt-3">We couldn't confirm your payment. Please try again or call us.</p>
          <button onClick={() => navigate("/menu")} className="mt-6 py-3.5 px-8 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide">Back To Menu</button>
        </>)}
      </div>
    </div>
  );
}
